// The shop map the garage drives out onto: carrier + location are chosen in
// the garage menu (Landing), so this is the map alone, with the choices shown
// as HUD chips whose "change" links reopen the garage on that menu.
// Deliberately session-free: nothing here is stored server-side, so no
// consent is needed until the user opts into the questionnaire.
import { Suspense, lazy } from "react";
import type { Insurer, ShopsResponse } from "../shared/types";
import type { MapIntro } from "./Shops";

const ShopsSection = lazy(() => import("./Shops"));

export function ShopFinder({
  insurer,
  insurerId,
  zip,
  coords,
  initialData,
  intro,
  onExit,
  onChange,
  onEstimate,
}: {
  insurer: Insurer | null;
  insurerId: string;
  zip: string | null;
  coords: { lat: number; lng: number } | null;
  /** Listings already fetched by the garage (its stat bars), keyed to this context. */
  initialData: ShopsResponse | null;
  intro?: MapIntro;
  onExit: () => void;
  onChange: (panel: "insurer" | "loc") => void;
  onEstimate: (insurerId: string, zip: string | null, lat: number | null, lng: number | null) => void;
}) {
  // Full-viewport game map: every control lives ON the map as HUD overlays.
  return (
    <Suspense fallback={<div className="gm-root" aria-busy="true" />}>
      <ShopsSection
        key={`${insurerId}-${coords ? `${coords.lat},${coords.lng}` : (zip ?? "none")}`}
        layout="full"
        ctx={{ insurer, zip, lat: coords?.lat ?? null, lng: coords?.lng ?? null }}
        defaultNetworkOnly
        initialData={initialData}
        intro={intro}
        hud={
          <>
            <button className="gm-home" onClick={onExit} aria-label="Back to the garage">
              ⌂
            </button>
            <div className="gm-loc">
              <span className="gm-loc-item">
                <b>{insurer?.name ?? "—"}</b>
                <button className="change-link" onClick={() => onChange("insurer")}>
                  change
                </button>
              </span>
              <span className="gm-loc-item">
                {coords ? <b>My location</b> : zip ? <b>ZIP {zip}</b> : <span>no location</span>}
                <button className="change-link" onClick={() => onChange("loc")}>
                  change
                </button>
              </span>
            </div>
          </>
        }
        underMap={
          <div className="estimate-cta gm-esti">
            <button
              className="cta"
              onClick={() => onEstimate(insurerId, zip, coords?.lat ?? null, coords?.lng ?? null)}
            >
              Pricing Guide
            </button>
          </div>
        }
      />
    </Suspense>
  );
}
